/**
 * Frontend Cache Service for Session-Based Caching
 * Provides in-memory caching that persists until week change or logout
 */

interface CacheEntry {
  data: any;
  timestamp: number;
  weekDate: string;
  cacheKey: string;
}

interface CacheStats {
  totalEntries: number;
  totalHits: number;
  totalMisses: number;
  lastHit?: string;
  lastMiss?: string;
}

class FrontendCacheService {
  private cache = new Map<string, CacheEntry>();
  private stats: CacheStats = {
    totalEntries: 0,
    totalHits: 0,
    totalMisses: 0
  };

  /**
   * Generate cache key for API calls
   * Format: {endpoint}_{weekDate}_{managerId}
   */
  private generateCacheKey(endpoint: string, weekDate: string, managerId?: string): string {
    const managerPart = managerId ? managerId : 'none';
    return `${endpoint}_${weekDate}_${managerPart}`;
  }

  /**
   * Extract week date from cache key for invalidation
   */
  private extractWeekFromKey(cacheKey: string): string {
    const parts = cacheKey.split('_');
    return parts.length >= 2 ? parts[1] : '';
  }

  /**
   * Check if cache entry is still valid (not expired)
   */
  private isExpired(entry: CacheEntry): boolean {
    // For now, entries don't expire by time, only by week change or logout
    // Could add time-based expiration here if needed
    return false;
  }

  /**
   * Get cached data or fetch using provided function
   */
  async getCachedOrFetch<T>(
    endpoint: string,
    weekDate: string,
    fetchFn: () => Promise<T>,
    managerId?: string
  ): Promise<T> {
    const cacheKey = this.generateCacheKey(endpoint, weekDate, managerId);
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && !this.isExpired(cached)) {
      console.log('🚀 [FRONTEND-CACHE] Cache hit:', cacheKey);
      this.stats.totalHits++;
      this.stats.lastHit = new Date().toISOString();
      return cached.data;
    }

    // Cache miss - fetch from backend
    console.log('📡 [FRONTEND-CACHE] Cache miss, fetching:', cacheKey);
    this.stats.totalMisses++;
    this.stats.lastMiss = new Date().toISOString();
    
    const data = await fetchFn();
    
    // Cache for session
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      weekDate,
      cacheKey
    });

    this.stats.totalEntries = this.cache.size;
    console.log('💾 [FRONTEND-CACHE] Cached data for session:', cacheKey);

    return data;
  }

  /**
   * Invalidate all entries for a specific week
   */
  invalidateWeek(weekDate: string): void {
    console.log('🗑️ [FRONTEND-CACHE] Invalidating week:', weekDate);
    
    let removedCount = 0;
    for (const [key, entry] of this.cache.entries()) {
      if (entry.weekDate === weekDate || this.extractWeekFromKey(key) === weekDate) {
        this.cache.delete(key);
        removedCount++;
      }
    }
    
    this.stats.totalEntries = this.cache.size;
    console.log(`✅ [FRONTEND-CACHE] Removed ${removedCount} entries for week ${weekDate}`);
  }

  /**
   * Clear entire cache (on logout)
   */
  clearAll(): void {
    console.log('🗑️ [FRONTEND-CACHE] Clearing entire cache');
    const entriesCount = this.cache.size;
    this.cache.clear();
    this.stats.totalEntries = 0;
    console.log(`✅ [FRONTEND-CACHE] Cleared ${entriesCount} cache entries`);
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return {
      ...this.stats,
      totalEntries: this.cache.size
    };
  }

  /**
   * Get all cache keys (for debugging)
   */
  getCacheKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Check if a specific key is cached
   */
  isCached(endpoint: string, weekDate: string, managerId?: string): boolean {
    const cacheKey = this.generateCacheKey(endpoint, weekDate, managerId);
    const cached = this.cache.get(cacheKey);
    return cached !== undefined && !this.isExpired(cached);
  }

  /**
   * Manually remove a specific cache entry
   */
  remove(endpoint: string, weekDate: string, managerId?: string): boolean {
    const cacheKey = this.generateCacheKey(endpoint, weekDate, managerId);
    const removed = this.cache.delete(cacheKey);
    if (removed) {
      this.stats.totalEntries = this.cache.size;
      console.log('🗑️ [FRONTEND-CACHE] Removed cache entry:', cacheKey);
    }
    return removed;
  }

  /**
   * Get cache size in MB (approximate)
   */
  getCacheSizeMB(): number {
    let totalSize = 0;
    for (const entry of this.cache.values()) {
      // Rough estimate of object size in memory
      totalSize += JSON.stringify(entry.data).length * 2; // Unicode chars are 2 bytes
    }
    return totalSize / (1024 * 1024);
  }

  /**
   * Log cache statistics
   */
  logStats(): void {
    const stats = this.getStats();
    const sizeMB = this.getCacheSizeMB();
    
    console.log('📊 [FRONTEND-CACHE] Cache Statistics:', {
      totalEntries: stats.totalEntries,
      totalHits: stats.totalHits,
      totalMisses: stats.totalMisses,
      hitRate: stats.totalHits + stats.totalMisses > 0 
        ? `${((stats.totalHits / (stats.totalHits + stats.totalMisses)) * 100).toFixed(1)}%`
        : '0%',
      sizeMB: `${sizeMB.toFixed(2)}MB`,
      lastHit: stats.lastHit,
      lastMiss: stats.lastMiss,
      cacheKeys: this.getCacheKeys()
    });
  }
}

// Singleton instance
let frontendCacheInstance: FrontendCacheService | null = null;

/**
 * Get frontend cache service instance
 */
export function getFrontendCache(): FrontendCacheService {
  if (!frontendCacheInstance) {
    frontendCacheInstance = new FrontendCacheService();
    console.log('✅ [FRONTEND-CACHE] Frontend cache service initialized');
  }
  return frontendCacheInstance;
}

/**
 * Clear frontend cache (utility function)
 */
export function clearFrontendCache(): void {
  const cache = getFrontendCache();
  cache.clearAll();
}

/**
 * Invalidate frontend cache for specific week (utility function)
 */
export function invalidateFrontendCacheWeek(weekDate: string): void {
  const cache = getFrontendCache();
  cache.invalidateWeek(weekDate);
}

export { FrontendCacheService };
export type { CacheEntry, CacheStats }; 